import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { 
  FileText, 
  Calendar, 
  MapPin, 
  User, 
  Calculator,
  Package,
  DollarSign,
  Check,
  X,
  Eye,
  Clock,
  Download
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const UserQuotes = ({ user }) => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [showQuoteDetails, setShowQuoteDetails] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (user?.email) {
      fetchMyQuotes();
    }
  }, [user]);

  const fetchMyQuotes = async () => {
    setLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      
      const response = await fetch(`${backendUrl}/api/quotes/my/quotes`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (data.success) {
        setQuotes(data.data.quotes || []);
      } else {
        throw new Error(data.data?.error || 'Failed to fetch quotes');
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: `Failed to fetch quotes: ${error.message}`
      });
    } finally {
      setLoading(false);
    }
  };

  const acceptQuote = async (quoteId) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      
      const response = await fetch(`${backendUrl}/api/quotes/accept/${quoteId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (data.success) {
        toast({
          title: "Success",
          description: "Quote accepted successfully! We'll be in touch soon."
        });
        fetchMyQuotes(); // Refresh quotes
      } else {
        throw new Error(data.data?.error || 'Failed to accept quote');
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: `Failed to accept quote: ${error.message}`
      });
    }
  };

  const rejectQuote = async (quoteId) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      
      const response = await fetch(`${backendUrl}/api/quotes/reject/${quoteId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (data.success) {
        toast({
          title: "Quote Rejected",
          description: "Quote has been rejected."
        });
        fetchMyQuotes(); // Refresh quotes
      } else {
        throw new Error(data.data?.error || 'Failed to reject quote');
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: `Failed to reject quote: ${error.message}`
      });
    }
  };

  const viewQuoteDetails = async (quoteId) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/quotes/${quoteId}`);
      const data = await response.json();
      
      if (data.success) {
        setSelectedQuote(data.data.quote);
        setShowQuoteDetails(true);
      } else {
        throw new Error(data.data?.error || 'Failed to fetch quote details');
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: `Failed to fetch quote details: ${error.message}`
      });
    }
  };

  const downloadQuotePDF = async (quoteId, customerName, quoteNumber) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/quotes/download-pdf/${quoteId}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/pdf',
        }
      });

      if (!response.ok) {
        throw new Error('Failed to download PDF');
      }

      // Create blob from response
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Set filename
      const filename = `Aston_Lily_Shutters_Quote_${quoteNumber || 'quote'}_${customerName || 'customer'}.pdf`;
      link.setAttribute('download', filename);
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up
      window.URL.revokeObjectURL(url);
      
      toast({
        title: "Success",
        description: "PDF quote downloaded successfully"
      });
      
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: `Failed to download PDF: ${error.message}`
      });
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'draft': return 'bg-gray-500';
      case 'sent': return 'bg-[#8CB89F]';
      case 'accepted': return 'bg-green-500';
      case 'rejected': return 'bg-red-500';
      case 'expired': return 'bg-orange-500';
      default: return 'bg-gray-500';
    }
  };

  const isQuoteExpired = (validUntil) => {
    return new Date(validUntil) < new Date();
  };

  const isQuoteActionable = (quote) => {
    return quote.status === 'sent' && !isQuoteExpired(quote.valid_until);
  };

  if (showQuoteDetails && selectedQuote) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Quote Details</h2>
            <p className="text-gray-600">Quote #{selectedQuote.quote_number}</p>
          </div>
          <Button 
            variant="outline" 
            onClick={() => setShowQuoteDetails(false)}
          >
            Back to My Quotes
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Quote Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Quote Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <p><strong>Quote Number:</strong> {selectedQuote.quote_number}</p>
                <p><strong>Status:</strong> 
                  <Badge className={`ml-2 ${getStatusBadgeColor(selectedQuote.status)}`}>
                    {selectedQuote.status}
                  </Badge>
                </p>
                <p><strong>Created:</strong> {new Date(selectedQuote.created_at).toLocaleDateString()}</p>
                <p><strong>Valid Until:</strong> {new Date(selectedQuote.valid_until).toLocaleDateString()}</p>
                {isQuoteExpired(selectedQuote.valid_until) && (
                  <p className="text-red-600 font-medium">⚠️ This quote has expired</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Quote Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calculator className="h-5 w-5" />
                Quote Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <p><strong>Windows:</strong> {selectedQuote.windows_count}</p>
                <p><strong>Total Area:</strong> {selectedQuote.total_area}m²</p>
                <p><strong>Subtotal:</strong> £{selectedQuote.subtotal}</p>
                {selectedQuote.overall_discount_amount > 0 && (
                  <p><strong>Discount:</strong> -£{selectedQuote.overall_discount_amount}</p>
                )}
                <p className="text-lg font-bold" style={{color: '#8CB89F'}}>
                  <strong>Total:</strong> £{selectedQuote.total_cost}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Customer Details */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Your Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <p><strong>Name:</strong> {selectedQuote.customer_name}</p>
                <p><strong>Email:</strong> {selectedQuote.user_email}</p>
                <p><strong>Postcode:</strong> {selectedQuote.customer_postcode}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Windows Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle>Windows Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {selectedQuote.windows.map((window, index) => (
                <div key={index} className="border rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <h4 className="font-semibold">{window.label}</h4>
                      <p>{window.width}{window.unit} x {window.height}{window.unit}</p>
                      <p>{window.area}m² - {window.type} window</p>
                    </div>
                    <div>
                      <p><strong>Shutter Colour:</strong> {window.shutter_colour}</p>
                      <p><strong>Louvre Size:</strong> {window.louvre_size}</p>
                    </div>
                    <div>
                      <p><strong>Control Type:</strong> {window.control_type}</p>
                      <p><strong>Hinge Colour:</strong> {window.hinge_colour}</p>
                    </div>
                    <div className="text-right">
                      <p><strong>Cost:</strong> £{window.cost}</p>
                      {window.discount_percentage > 0 && (
                        <>
                          <p className="text-green-600">Discount: {window.discount_percentage}%</p>
                          <p><strong>Final:</strong> £{window.discounted_cost}</p>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {selectedQuote.notes && (
          <Card>
            <CardHeader>
              <CardTitle>Additional Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700">{selectedQuote.notes}</p>
            </CardContent>
          </Card>
        )}

        {/* Actions */}
        <div className="flex gap-4 justify-center">
          <Button 
            variant="outline"
            onClick={() => downloadQuotePDF(selectedQuote.id, selectedQuote.customer_name, selectedQuote.quote_number)}
            className="px-8"
          >
            <Download className="mr-2 h-4 w-4" />
            Download PDF
          </Button>
          
          {isQuoteActionable(selectedQuote) && (
            <>
              <Button 
                onClick={() => acceptQuote(selectedQuote.id)}
                style={{backgroundColor: '#8CB89F'}}
                className="hover:opacity-90 px-8"
              >
                <Check className="mr-2 h-4 w-4" />
                Accept Quote
              </Button>
              <Button 
                variant="outline" 
                onClick={() => rejectQuote(selectedQuote.id)}
                className="px-8"
              >
                <X className="mr-2 h-4 w-4" />
                Reject Quote
              </Button>
            </>
          )}
        </div>

        {selectedQuote.status === 'accepted' && (
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-6 text-center">
              <Check className="h-12 w-12 mx-auto mb-4 text-green-600" />
              <h3 className="text-lg font-semibold text-green-800 mb-2">Quote Accepted!</h3>
              <p className="text-green-700">
                Thank you for accepting our quote. We'll be in touch soon to arrange the next steps 
                and schedule your installation.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">My Quotes</h2>
          <p className="text-gray-600">View and manage your shutter quotes</p>
        </div>
      </div>

      {loading && <p>Loading your quotes...</p>}

      <div className="space-y-4">
        {quotes.map((quote) => (
          <Card key={quote.id} className={isQuoteExpired(quote.valid_until) ? 'opacity-75' : ''}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-4 mb-2">
                    <h3 className="text-lg font-semibold">Quote #{quote.quote_number}</h3>
                    <Badge className={getStatusBadgeColor(quote.status)}>
                      {quote.status}
                    </Badge>
                    {isQuoteExpired(quote.valid_until) && (
                      <Badge className="bg-orange-500">Expired</Badge>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm text-gray-600">
                    <div>
                      <Package className="inline h-4 w-4 mr-1" />
                      {quote.windows_count} windows
                    </div>
                    <div>
                      <Calculator className="inline h-4 w-4 mr-1" />
                      {quote.total_area}m² total area
                    </div>
                    <div>
                      <DollarSign className="inline h-4 w-4 mr-1" />
                      £{quote.total_cost}
                    </div>
                    <div>
                      <Calendar className="inline h-4 w-4 mr-1" />
                      Valid until: {new Date(quote.valid_until).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="mt-2 text-sm text-gray-500">
                    Created: {new Date(quote.created_at).toLocaleDateString()}
                    {quote.sent_at && ` | Sent: ${new Date(quote.sent_at).toLocaleDateString()}`}
                  </div>
                </div>
                
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => viewQuoteDetails(quote.id)}
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    View Details
                  </Button>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => downloadQuotePDF(quote.id, quote.customer_name, quote.quote_number)}
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                  
                  {isQuoteActionable(quote) && (
                    <>
                      <Button
                        size="sm"
                        onClick={() => acceptQuote(quote.id)}
                        style={{backgroundColor: '#8CB89F'}}
                        className="hover:opacity-90"
                      >
                        <Check className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => rejectQuote(quote.id)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {quotes.length === 0 && !loading && (
        <Card>
          <CardContent className="p-12 text-center">
            <FileText className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg font-semibold mb-2">No quotes yet</h3>
            <p className="text-gray-600 mb-4">
              You haven't received any quotes yet. Complete our calculator to request a quote.
            </p>
            <Button 
              onClick={() => window.location.href = '/calculator'}
              style={{backgroundColor: '#8CB89F'}}
              className="hover:opacity-90"
            >
              <Calculator className="mr-2 h-4 w-4" />
              Get Quote
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default UserQuotes;